import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Inject, OnInit, Output } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MAT_DIALOG_DATA, MatDialogModule } from "@angular/material/dialog";
import { MatDividerModule } from "@angular/material/divider";
import { UserInfo } from "@models/user";
import {
  cpfValidator,
  nameValidator,
  phoneNumberValidator,
} from "@shared/validatos";
import { cnpjValidator } from "@shared/validatos/cnpj.validator";
import { NgxMaskDirective, provideNgxMask } from "ngx-mask";
import { ButtonComponent } from "../../../../../shared/components/button/button.component";
import { DdiSelectComponent } from "../../../../../shared/components/ddi-select/ddi-select.component";
import { PageHeaderComponent } from "../../../../../shared/components/page-header/page-header.component";
import {
  BecomeOrganizarIndividualRequest,
  BecomeOrganizerCompanyRequest,
} from "../../../../services/types/become-organizer.type";

type DialogData = {
  userData: UserInfo;
};

@Component({
    selector: "app-enable-producer-dialog",
    imports: [
        PageHeaderComponent,
        CommonModule,
        ReactiveFormsModule,
        DdiSelectComponent,
        MatDividerModule,
        NgxMaskDirective,
        MatCheckboxModule,
        MatButtonModule,
        ButtonComponent,
        MatDialogModule,
    ],
    providers: [provideNgxMask()],
    templateUrl: "./enable-producer-dialog.component.html",
    styleUrl: "./enable-producer-dialog.component.scss"
})
export class EnableProducerDialogComponent implements OnInit {
  keepAsIndividualControl = new FormControl(false);
  acceptUserTerms = new FormControl(false, Validators.requiredTrue);

  individualForm = this.fb.group({
    cpf: ["", [Validators.required, cpfValidator]],
    name: ["", [Validators.required, nameValidator]],
    ddi: ["", [Validators.required]],
    phone: ["", [Validators.required, phoneNumberValidator]],
  });

  companyForm = this.fb.group({
    cnpj: ["", [Validators.required, cnpjValidator]],
    companyName: ["", [Validators.required]],
  });

  @Output() openContractDialogClick = new EventEmitter();
  @Output() submitEnableProducerForm = new EventEmitter<
    BecomeOrganizarIndividualRequest | BecomeOrganizerCompanyRequest
  >();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.populeteUserInfo();
  }

  handleFormSubmit() {
    this.acceptUserTerms.markAsTouched();
    this.companyForm.markAllAsTouched();
    this.individualForm.markAllAsTouched();
    if (!this.acceptUserTerms.valid) {
      return;
    }

    if (this.keepAsIndividualControl.value) {
      if (this.individualForm.invalid) {
        return;
      }
      this.handleIndividualForm();
    } else {
      if (this.companyForm.invalid) {
        return;
      }

      this.handleCompanyForm();
    }
  }

  openContractDialog() {
    this.openContractDialogClick.emit();
  }

  private handleIndividualForm() {
    const { cpf, ddi, name, phone } = this.individualForm.value;

    if (!cpf || !ddi || !name || !phone) {
      return;
    }
    const nameArray = name!
      .replace(/\s{2,}/g, " ")
      .trim()
      .split(" ");

    const firstName = nameArray[0];
    const lastName = nameArray?.slice(1)?.join(" ");

    const payload: BecomeOrganizarIndividualRequest = {
      user: {
        cpf,
        phone: `${ddi} ${phone}`,
        name: firstName,
        last_name: lastName,
      },
    };

    this.submitEnableProducerForm.emit(payload);
  }

  private handleCompanyForm() {
    const { cnpj, companyName } = this.companyForm.value;

    if (!cnpj || !companyName) {
      return;
    }

    const payload: BecomeOrganizerCompanyRequest = {
      user: {
        cnpj,
        company_name: companyName,
      },
    };

    this.submitEnableProducerForm.emit(payload);
  }

  private populeteUserInfo() {
    const { userData } = this.data;
    const { ddi, phoneNumber } = this.extractCountryCodeAndNumber(
      userData.phone_number,
    );

    this.individualForm.patchValue({
      cpf: userData.cpf,
      ddi: ddi,
      phone: phoneNumber,
      name: `${userData.name} ${userData.last_name}`,
    });
  }

  private extractCountryCodeAndNumber(celular: string): {
    ddi: string;
    phoneNumber: string;
  } {
    if (!celular) {
      return { ddi: "", phoneNumber: "" };
    }
    if (!celular?.startsWith("+")) {
      return { ddi: "+55", phoneNumber: celular.slice(-11) };
    }

    // O DDI é o primeiro código depois do '+'. Vamos separá-lo.
    const ddi = celular.slice(0, celular.length - 11); // Mantém o início (+55 no exemplo) antes dos 11 dígitos finais.
    const phoneNumber = celular.slice(-11); // Pega os últimos 11 dígitos do número.

    return {
      ddi,
      phoneNumber,
    };
  }
}
